// Connexion d'un usager (POST /public/connexion) : identifiant (téléphone ou courriel) et mot de
// passe ; liens vers l'inscription et le mot de passe oublié ; comptes de présentation usagers
// hors production (groupes ou profils « usager » ou « citoyen » de docs/realisation/comptes-presentation.md).
// « Se connecter avec mon NIP » vient en premier choix quand le service Identité & NIP est configuré.
import { Bandeau, Bouton, Champ, Formulaire } from '@cc/ui';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { ErreurApi, messageErreur } from '../../../api/erreurs';
import { useComptesPresentation } from '../../acces/ComptesPresentation';
import { useCollectiviteChoisie } from '../collectivite';
import { BoutonConnexionNip, RetourNip } from './ConnexionNip';
import { EnTeteCitoyen } from '../commun';
import { useSessionUsager } from '../session/FournisseurSessionUsager';
import styles from '../citoyen.module.css';

function messageConnexion(e: unknown): string {
  if (e instanceof ErreurApi) {
    if (e.code === 'identifiants_invalides') return 'Identifiant ou mot de passe incorrect.';
    if (e.code === 'trop_de_tentatives') {
      const secondes = (e.details as { reessayer_dans_secondes?: number } | undefined)?.reessayer_dans_secondes;
      return `Connexion temporairement verrouillée après plusieurs échecs${secondes ? ` : réessayez dans ${Math.ceil(secondes / 60)} minute(s)` : ''}.`;
    }
  }
  return messageErreur(e);
}

const USAGER = /usager|citoyen/i;

/** Comptes de présentation de type usager : boutons qui remplissent identifiant et mot de passe. */
export function ComptesPresentationUsagers({ onChoisir, desactive }: { onChoisir: (identifiant: string, motDePasse: string) => void; desactive?: boolean }) {
  const comptes = useComptesPresentation();
  const liste = (comptes.data?.groupes ?? []).flatMap((g) => g.comptes.filter((c) => USAGER.test(g.titre) || USAGER.test(c.profil)).map((c) => ({ ...c, groupe: g.titre })));
  if (liste.length === 0) return null;
  return (
    <section aria-labelledby="presentation-usagers-titre" className={styles.pile}>
      <h2 id="presentation-usagers-titre" className={styles.titreSection}>
        Comptes de présentation
      </h2>
      <p className={styles.aide}>Comptes fictifs des bases de présentation, avec leur mot de passe : un clic remplit le formulaire.</p>
      <ul className={styles.liste}>
        {liste.map((c) => (
          <li key={c.identifiant}>
            <button type="button" className={styles.ligne} disabled={desactive} onClick={() => onChoisir(c.identifiant, c.mot_de_passe)} aria-label={`${c.profil}, ${c.identifiant}, mot de passe ${c.mot_de_passe}`}>
              <span className={styles.ligneTexte}>
                <span className={styles.ligneTitre}>{c.profil}</span>
                <span className={styles.ligneSous}>{c.identifiant}</span>
                <span className={styles.ligneSous}>Mot de passe : {c.mot_de_passe}</span>
              </span>
              <span className={styles.ligneDroite}>Remplir</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function PageConnexionUsager() {
  const { connecter, etat, expiree } = useSessionUsager();
  const [choisie] = useCollectiviteChoisie();
  const navigate = useNavigate();
  const location = useLocation();
  const destination = (location.state as { depuis?: string } | null)?.depuis ?? (choisie ? `/citoyen/${choisie.code}` : '/citoyen');
  const [identifiant, setIdentifiant] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [erreursChamps, setErreursChamps] = useState<{ champ: string; message: string }[]>([]);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (etat === 'connecte') navigate(destination, { replace: true });
  }, [etat, navigate, destination]);

  const soumettre = async () => {
    const manquants = [
      !identifiant.trim() ? { champ: 'identifiant-usager', message: 'Saisissez votre téléphone ou votre courriel' } : null,
      !motDePasse ? { champ: 'mot-de-passe-usager', message: 'Saisissez votre mot de passe' } : null,
    ].filter((x): x is { champ: string; message: string } => x !== null);
    setErreursChamps(manquants);
    if (manquants.length > 0) return;
    setErreur(null);
    setEnCours(true);
    try {
      await connecter(identifiant, motDePasse);
      navigate(destination, { replace: true });
    } catch (e) {
      setErreur(messageConnexion(e));
    } finally {
      setEnCours(false);
    }
  };
  const erreurChamp = (champ: string) => erreursChamps.find((e) => e.champ === champ)?.message ?? null;

  return (
    <>
      <EnTeteCitoyen titre="Se connecter" sousTitre="Avec le téléphone ou le courriel de votre compte usager." retour={choisie ? `/citoyen/${choisie.code}` : '/citoyen'} libelleRetour="Accueil" />
      {expiree ? (
        <Bandeau type="attention" titre="Session expirée">
          Reconnectez-vous pour continuer. Les brouillons en cours sont conservés.
        </Bandeau>
      ) : null}
      {etat === 'compte_agent' ? <Bandeau type="info">Un compte agent est connecté dans ce navigateur. Se connecter ici avec un compte usager fermera sa session.</Bandeau> : null}
      <RetourNip />
      <BoutonConnexionNip />
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      <Formulaire
        libelle="Connexion usager"
        onSoumettre={() => void soumettre()}
        erreurs={erreursChamps}
        actions={
          <Bouton type="submit" chargement={enCours} libelleChargement="Vérification en cours" pleineLargeur>
            Se connecter
          </Bouton>
        }
      >
        <Champ
          id="identifiant-usager"
          libelle="Téléphone ou courriel"
          autoComplete="username"
          inputMode="email"
          value={identifiant}
          onChange={(e) => setIdentifiant(e.target.value)}
          erreur={erreurChamp('identifiant-usager')}
          required
        />
        <Champ
          id="mot-de-passe-usager"
          libelle="Mot de passe"
          type="password"
          autoComplete="current-password"
          value={motDePasse}
          onChange={(e) => setMotDePasse(e.target.value)}
          erreur={erreurChamp('mot-de-passe-usager')}
          required
        />
        <p className={styles.aide}>
          <Link to="/citoyen/mot-de-passe">Mot de passe oublié</Link>
        </p>
      </Formulaire>
      <p>
        Pas encore de compte ? <Link to="/citoyen/inscription">Créer un compte</Link> : il sert pour toutes les collectivités.
      </p>
      <ComptesPresentationUsagers
        desactive={enCours}
        onChoisir={(id, mdp) => {
          setIdentifiant(id);
          setMotDePasse(mdp);
          setErreur(null);
          setErreursChamps([]);
        }}
      />
    </>
  );
}
